import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Loader2, FileText, ExternalLink } from "lucide-react";
import { useGetLatestTermsOfServiceQuery, useUpdateUserTosAcceptanceMutation } from "@/redux/api/legal.rtkq";
import { Link } from "react-router";

interface TosAcceptanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccepted?: () => void;
  currentVersion?: string;
  forceAccept?: boolean;
}

export const TosAcceptanceModal: React.FC<TosAcceptanceModalProps> = ({
  isOpen,
  onClose,
  onAccepted,
  currentVersion,
  forceAccept = false,
}) => {
  const { data: terms, isLoading } = useGetLatestTermsOfServiceQuery();
  const [updateTosAcceptance, { isLoading: isUpdating }] = useUpdateUserTosAcceptanceMutation();
  const [tosAccepted, setTosAccepted] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setTosAccepted(false);
    }
  }, [isOpen]);

  // Check if user needs to accept (if current version is different from latest)
  const needsAcceptance = terms && (!currentVersion || currentVersion !== terms.version);

  const handleAccept = async () => {
    if (!terms || !tosAccepted) return;

    try {
      await updateTosAcceptance({
        version: terms.version,
        ipAddress: undefined, // Backend will extract from request headers
      }).unwrap();

      if (onAccepted) {
        onAccepted();
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Failed to accept Terms of Service:", error);
      // Could show an error message here
    }
  };

  const canAccept = tosAccepted;

  if (!needsAcceptance && !forceAccept) {
    return null;
  }

  return (
    <Dialog open={isOpen && !!needsAcceptance} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] rtl" showCloseButton={!forceAccept}>
        <DialogHeader>
          <DialogTitle className="text-xl text-chen-900">
            {forceAccept ? "תנאי שימוש" : "תנאי שימוש - עודכן לאחרונה"}
          </DialogTitle>
          <DialogDescription className="text-base text-right">
            {forceAccept
              ? "יש לאשר את תנאי השימוש העדכניים כדי להמשיך להשתמש במערכת"
              : "עודכנו תנאי השימוש שלנו. אנא קרא ואשר את הגרסה החדשה."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Version Info */}
          {terms && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  Version {terms.version}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {terms.effectiveDate &&
                    (() => {
                      try {
                        const date = terms.effectiveDate instanceof Date ? terms.effectiveDate : new Date(terms.effectiveDate);
                        return !isNaN(date.getTime()) ? date.toLocaleDateString("he-IL") : "";
                      } catch {
                        return "";
                      }
                    })()}
                </span>
              </div>
            </div>
          )}

          {/* Terms Link */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="mr-2">טוען...</span>
            </div>
          ) : terms ? (
            <div className="p-6 text-center bg-muted/30 rounded-lg border border-border">
              <p className="text-sm text-muted-foreground mb-4">
                אנא קרא/י את תנאי השימוש ומדיניות הפרטיות לפני המשך השימוש במערכת
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                <Button variant="outline" size="sm" asChild>
                  <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    תנאי שימוש
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    מדיניות פרטיות
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-4 text-center text-muted-foreground">שגיאה בטעינת תנאי השימוש</div>
          )}

          {/* Acceptance Checkbox */}
          <div className="flex items-start space-x-2 space-x-reverse">
            <Checkbox
              id="modal-tos-checkbox"
              checked={tosAccepted}
              onCheckedChange={(checked) => setTosAccepted(!!checked)}
              disabled={!terms}
            />
            <label htmlFor="modal-tos-checkbox" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
              קראתי ואני מסכים/ה לתנאי השימוש ולמדיניות הפרטיות
            </label>
          </div>

          {/* Action Buttons */}
          <DialogFooter className="gap-2">
            {!forceAccept && (
              <Button variant="outline" onClick={onClose} disabled={isUpdating}>
                ביטול
              </Button>
            )}
            <Button onClick={handleAccept} disabled={!canAccept || isUpdating || !terms} className="min-w-[120px]">
              {isUpdating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  מעבד...
                </>
              ) : (
                "אני מסכים/ה"
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TosAcceptanceModal;
